import * as React from "react";

import { Create as EditIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";

import { DeleteIcon } from "src/common/components/table/toolbarIcons";
import { ConfirmIconButton } from "src/common/components/ui/ConfirmButton";
import { UpdateTemplateDialog, useDeleteTemplate, useUpdateTemplate } from "src/domain/core/templates";
import { LaunchDialog } from "src/domain/core/workflow";
import { getGenericTooltipType } from "src/lib/table/tooltip";

interface WTSProps {
  templates: Templates;
  nodes: Nodes;
  blueprints: WorkflowBlueprintDesc[];
  disabled: boolean;
  disks: Disk[];
  disksError: Error;
}

const NoTemplatesText = styled(Typography)(({ theme }) => ({
  marginTop: "4px",
  marginLeft: "12px",
  marginRight: "12px",
  color: theme.palette.text.secondary,
}));

const MenuTitle = styled(Typography)(({ theme }) => ({
  marginTop: 6,
  marginLeft: 16,
  marginBottom: 8,
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

export const TemplateSelect: React.FC<WTSProps> = ({ templates, nodes, blueprints, disabled, disks, disksError }) => {
  const updateTemplate = useUpdateTemplate();

  const [selectOpen, setSelectOpen] = React.useState<boolean>(false);
  const [launchModalOpen, setLaunchModalOpen] = React.useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState<Template>(null);
  const [selectWidth, setSelectWidth] = React.useState(0);
  const selectRef = React.useRef(null);

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 450,
        width: selectWidth,
        minWidth: 350,
      },
    },
  };

  const handleEdit = (template: Template) => {
    setSelectOpen(false);
    setSelectedTemplate(template);
    setEditModalOpen(true);
  };

  const handleLaunch = (template: Template) => {
    setSelectOpen(false);
    setSelectedTemplate(template);
    setLaunchModalOpen(true);
  };

  const hasNoTemplates = () => {
    if (!templates) {
      return false;
    }

    const hasNoUserTemplates = !templates.user || templates.user.length == 0;
    const hasNoOrgTemplates = !templates.organization || templates.organization.length == 0;

    return hasNoUserTemplates && hasNoOrgTemplates;
  };

  return (
    <>
      <FormControl sx={{ width: "100%" }}>
        <InputLabel id="wf-template-select_label">Select Template</InputLabel>
        <Select
          data-testid="select-template"
          labelId="wf-template-select_label"
          id="wf-template-select"
          ref={selectRef}
          value={""}
          open={selectOpen}
          onClick={() => {
            if (!disabled) {
              setSelectWidth(selectRef.current.offsetWidth);
              setSelectOpen(!selectOpen);
            }
          }}
          input={<OutlinedInput label="Select Template" />}
          displayEmpty
          disabled={disabled}
          MenuProps={MenuProps}
        >
          {hasNoTemplates() && (
            <NoTemplatesText onClick={(e) => e.stopPropagation()}>
              No Templates Available. Create a Template from a Blueprint below.
            </NoTemplatesText>
          )}
          {templates?.user?.length > 0 && (
            <div>
              <MenuTitle onClick={(e) => e.stopPropagation()}>MY TEMPLATES</MenuTitle>
              {templates.user.map((template) => (
                <TemplateRow
                  key={template.templateName}
                  template={template}
                  blueprints={blueprints}
                  type="user"
                  setSelectOpen={setSelectOpen}
                  handleEdit={handleEdit}
                  handleLaunch={handleLaunch}
                />
              ))}
            </div>
          )}
          {templates?.user?.length > 0 && templates?.organization?.length > 0 && (
            <Divider onClick={(e) => e.stopPropagation()} />
          )}
          {templates?.organization?.length > 0 && (
            <div>
              <MenuTitle sx={{ mt: 3 }} onClick={(e) => e.stopPropagation()}>
                ORGANIZATION TEMPLATES
              </MenuTitle>
              {templates.organization.map((template) => (
                <TemplateRow
                  key={template.id}
                  template={template}
                  blueprints={blueprints}
                  type="organization"
                  setSelectOpen={setSelectOpen}
                  handleEdit={handleEdit}
                  handleLaunch={handleLaunch}
                />
              ))}
            </div>
          )}
        </Select>
      </FormControl>
      {launchModalOpen && (
        <LaunchDialog
          modalOpen={launchModalOpen}
          setModalOpen={setLaunchModalOpen}
          template={selectedTemplate}
          templates={[]}
          blueprintId={selectedTemplate?.blueprintId}
          nodes={nodes}
          blueprints={blueprints}
          disks={disks}
          disksError={disksError}
        />
      )}
      <UpdateTemplateDialog
        modalOpen={editModalOpen}
        setModalOpen={setEditModalOpen}
        template={selectedTemplate}
        templates={[...(templates?.user || [])]}
        nodes={nodes}
        blueprints={blueprints}
        disks={disks}
        disksError={disksError}
        updateTemplate={updateTemplate}
      />
    </>
  );
};

interface RowTextProps {
  rowName: string;
  blueprint: string;
}

const SubText = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  color: theme.palette.text.secondary,
}));

const RowText: React.FC<RowTextProps> = ({ rowName, blueprint }) => {
  return (
    <Stack spacing={0}>
      <Typography>{rowName}</Typography>
      <SubText sx={{ fontSize: 13 }}>{blueprint}</SubText>
    </Stack>
  );
};

interface RowProps {
  template: Template;
  blueprints: WorkflowBlueprintDesc[];
  type: string;
  setSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleEdit: (template: Template) => void;
  handleLaunch: (template: Template) => void;
}

const TemplateRow: React.FC<RowProps> = ({ template, blueprints, type, setSelectOpen, handleEdit, handleLaunch }) => {
  const deleteTemplate = useDeleteTemplate();
  const [deleting, setDeleting] = React.useState<boolean>(false);

  const deleteWorkflowTemplate = async () => {
    setDeleting(true);
    setSelectOpen(false);
    await deleteTemplate(template.id, template.templateName);
    setDeleting(false);
  };

  const blueprintAvailable = React.useMemo(() => {
    return blueprints?.find((blueprint) => blueprint.name == template?.blueprintName);
  }, [template, blueprints]);

  return (
    <>
      <MenuItem
        key={template.templateName}
        value={template.templateName}
        sx={{ py: 3 }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        disableRipple
      >
        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ textWrap: "auto" }}>
            <RowText rowName={template.templateName} blueprint={template.blueprintName} />
          </Box>
          <Box sx={{ float: "right" }}>
            {type === "user" && (
              <>
                <ConfirmIconButton
                  data-testid={template.templateName + `-template-delete`}
                  onSubmit={async () => await deleteWorkflowTemplate()}
                  showModalFunc={() => true}
                  confirmText={`Are you sure you want to delete the ${template.templateName} template?`}
                  confirmButtonText="Yes"
                  modalSx={{
                    borderRadius: "0px",
                    position: "fixed",
                  }}
                  tooltip={"Delete Workflow Template"}
                  icon={DeleteIcon(getGenericTooltipType(1, false, deleting))}
                  name={`Delete workflow template`}
                  type="submit"
                  size="medium"
                  disabled={false}
                />
                <IconButton
                  data-testid={template.templateName + `-template-edit`}
                  onClick={() => {
                    handleEdit(template);
                  }}
                >
                  <Tooltip title="Edit Workflow Template">
                    <EditIcon />
                  </Tooltip>
                </IconButton>
              </>
            )}
            <Button
              size="small"
              variant="contained"
              sx={{ maxHeight: 30, mt: 1 }}
              data-testid={template.templateName + `_${type}-template-launch`}
              disabled={!blueprintAvailable}
              onClick={() => handleLaunch(template)}
            >
              Launch
            </Button>
          </Box>
        </Box>
      </MenuItem>
    </>
  );
};
